import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { useForm, useWatch } from 'react-hook-form'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useQueryGetDepositPromotionNamesForSelector from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionNamesForSelector'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { AxiosError } from 'axios'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import { useMutateDepositTransaction } from '@/modules/transaction/api/hooks/useMutateDepositTransaction'
import useQueryGetPlayerList from '@/modules/manage-player/api/hooks/useQueryGetPlayerList'
import { CreateManualDepositTransactionDtoManualDepositType } from '@/schema'

const schema = z.object({
	amount: z
		.union([
			z.string().min(1, 'กรุณากรอกจำนวนเงิน').transform(Number),
			z.number(),
		])
		.refine((val) => typeof val === 'number' && !isNaN(val) && val > 0, {
			message: 'จำนวนเงินต้องมากกว่า 0',
		}),
	phoneNumber: z
		.string()
		.min(1, 'กรุณากรอกเบอร์โทรศัพท์')
		.regex(/^\d{9,15}$/, 'เบอร์โทรศัพท์ไม่ถูกต้อง'),
	manualDepositType: z.nativeEnum(
		CreateManualDepositTransactionDtoManualDepositType,
	),
	promotionId: z.string().optional(),
	bankProviderName: z.string().optional(),
	accountNumber: z.string().optional(),
	accountName: z.string().optional(),
	note: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

interface CreateTransactionDialogProps {
	onSuccess?: () => void
}

export function CreateTransactionDialog({
	onSuccess,
}: CreateTransactionDialogProps) {
	const [open, setOpen] = useState(false)
	const form = useForm<FormValues>({
		defaultValues: {
			phoneNumber: '',
			manualDepositType:
				CreateManualDepositTransactionDtoManualDepositType.REGULAR,
			promotionId: '',
			bankProviderName: '',
			accountNumber: '',
			accountName: '',
			note: '',
		},
		resolver: zodResolver(schema),
	})
	const { manualDeposit } = useMutateDepositTransaction()
	const {
		data: promotionData,
		isLoading: isPromotionLoading,
		isError: isPromotionError,
	} = useQueryGetDepositPromotionNamesForSelector()

	const phoneNumber = useWatch({
		control: form.control,
		name: 'phoneNumber',
	})

	const { data: playerData, isLoading: isPlayerLoading } =
		useQueryGetPlayerList(
			{
				skip: 0,
				take: 1,
				search: phoneNumber || undefined,
			},
			{
				enabled: phoneNumber?.length === 10,
			} as Parameters<typeof useQueryGetPlayerList>[1],
		)

	const onSubmit = async (values: FormValues) => {
		try {
			await manualDeposit.mutateAsync({
				amount: Number(values.amount),
				playerPhone: String(values.phoneNumber),
				manualDepositType: values.manualDepositType,
				promotionId: values.promotionId
					? String(values.promotionId)
					: undefined,
				bankProviderName: values.bankProviderName
					? String(values.bankProviderName)
					: undefined,
				accountNumber: values.accountNumber
					? String(values.accountNumber)
					: undefined,
				accountName: values.accountName
					? String(values.accountName)
					: undefined,
				note: values.note ? String(values.note) : undefined,
			})
			toast.success('สร้าง transaction สำเร็จ')
			setOpen(false)
			form.reset()
			if (onSuccess) onSuccess()
		} catch (e: unknown) {
			if (e instanceof AxiosError) {
				showErrorToast(
					e.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง',
				)
			} else {
				showErrorToast('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง')
			}
		}
	}

	return (
		<>
			<Button onClick={() => setOpen(true)}>สร้างรายการฝาก</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0">
					<DialogHeader className="shrink-0 px-6 pb-4 pt-6">
						<DialogTitle>สร้างฝากเงิน</DialogTitle>
					</DialogHeader>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="space-y-4 overflow-y-auto px-6 pb-6"
						>
							<FormField
								control={form.control}
								name="phoneNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>เบอร์โทรศัพท์</FormLabel>
										<FormControl>
											<Input {...field} />
										</FormControl>
										<FormMessage />
										{phoneNumber?.length === 10 && (
											<>
												{isPlayerLoading ? (
													<p className="text-sm text-muted-foreground">
														กำลังค้นหาผู้เล่น...
													</p>
												) : playerData?.total === 1 &&
												  playerData?.data?.[0]?.bank?.accountName ? (
													<p className="text-sm text-green-600 dark:text-green-400">
														พบผู้เล่น: {playerData.data[0].bank.accountName}
													</p>
												) : phoneNumber?.length === 10 &&
												  playerData?.total !== 1 ? (
													<p className="text-sm text-muted-foreground">
														ไม่พบผู้เล่นหรือพบมากกว่า 1 คน
													</p>
												) : null}
											</>
										)}
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="manualDepositType"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ประเภทการฝาก</FormLabel>
										<FormControl>
											<Select
												value={field.value}
												onValueChange={field.onChange}
											>
												<SelectTrigger className="w-full">
													<SelectValue placeholder="เลือกประเภทการฝาก" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem
														value={
															CreateManualDepositTransactionDtoManualDepositType.REGULAR
														}
													>
														ฝากปกติ
													</SelectItem>
													<SelectItem
														value={
															CreateManualDepositTransactionDtoManualDepositType.FREE_CREDIT
														}
													>
														เครดิตฟรี
													</SelectItem>
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>จำนวนเงิน</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												value={field.value}
												onChange={(e) => field.onChange(e.target.value)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="promotionId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>โปรโมชั่น (ถ้ามี)</FormLabel>
										<FormControl>
											<Select
												value={field.value || ''}
												onValueChange={field.onChange}
												disabled={isPromotionLoading || isPromotionError}
											>
												<SelectTrigger className="w-full">
													<SelectValue
														placeholder={
															isPromotionLoading
																? 'กำลังโหลด...'
																: 'เลือกโปรโมชั่น'
														}
													/>
												</SelectTrigger>
												<SelectContent
													className={'max-h-[300px] overflow-y-auto'}
												>
													<SelectItem value="">ไม่เลือกโปรโมชั่น</SelectItem>
													{promotionData?.data?.map((promo) => (
														<SelectItem key={promo.id} value={promo.id}>
															{promo.name}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<BankSelector
								control={form.control}
								name="bankProviderName"
								required={false}
							/>
							<FormField
								control={form.control}
								name="accountNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หมายเลขบัญชี (ถ้ามี)</FormLabel>
										<FormControl>
											<Input {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="accountName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ชื่อบัญชี (ถ้ามี)</FormLabel>
										<FormControl>
											<Input {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="note"
								render={({ field }) => (
									<FormItem>
										<FormLabel>หมายเหตุ (ถ้ามี)</FormLabel>
										<FormControl>
											<Textarea
												{...field}
												placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
												className="min-h-[80px]"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								className="w-full"
								disabled={manualDeposit.isPending}
							>
								{manualDeposit.isPending ? 'กำลังสร้าง...' : 'ยืนยัน'}
							</Button>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
